import React, { ReactNode } from 'react';
import { alpha, makeStyles, Theme } from '@material-ui/core/styles';

const INLINE_TOKEN_PATTERN =
    /(\*\*[^*]+\*\*|`[^`]+`|\[[^\]]+\]\(https?:\/\/[^)]+\))/g;
const LINK_PATTERN = /^\[([^\]]+)\]\((https?:\/\/[^)]+)\)$/;
const HEADING_PATTERN = /^#{1,3}\s+(.+)/;
const BULLET_PATTERN = /^[-*•]\s+(.+)/;
const BOLD_MARK = '**';
const CODE_BACKGROUND_ALPHA = 0.1;
const CODE_MARK = '`';

const useStyles = makeStyles((theme: Theme) => ({
    markdown: {
        whiteSpace: 'normal',
        '& p': { margin: '0 0 8px' },
        '& p:last-child': { marginBottom: 0 },
        '& ul, & ol': { margin: '6px 0 8px', paddingLeft: 20 },
        '& li': { marginBottom: 4, paddingLeft: 2 },
        '& strong': { fontWeight: 700 },
        '& h1, & h2, & h3': {
            fontSize: 14,
            lineHeight: 1.35,
            margin: '10px 0 6px',
        },
        '& h1:first-child, & h2:first-child, & h3:first-child': {
            marginTop: 0,
        },
        '& code': {
            backgroundColor: alpha(
                theme.palette.primary.main,
                CODE_BACKGROUND_ALPHA
            ),
            borderRadius: 4,
            fontFamily: 'monospace',
            fontSize: 12,
            padding: '1px 4px',
        },
        '& pre': {
            backgroundColor: theme.palette.action.hover,
            borderRadius: 8,
            maxWidth: '100%',
            overflowX: 'auto',
            padding: 10,
        },
        '& pre code': { backgroundColor: 'transparent', padding: 0 },
        '& blockquote': {
            borderLeft: `3px solid ${theme.palette.secondary.light}`,
            color: theme.palette.text.secondary,
            margin: '8px 0',
            paddingLeft: 10,
        },
        '& a': { color: theme.palette.primary.main, fontWeight: 600 },
    },
    listItem: {
        display: 'grid',
        gap: 7,
        gridTemplateColumns: '10px 1fr',
        marginBottom: 4,
    },
    spacer: { height: 4 },
}));

const isWrapped = (part: string, mark: string) =>
    part.startsWith(mark) && part.endsWith(mark);

const renderInline = (text: string): ReactNode[] =>
    text.split(INLINE_TOKEN_PATTERN).map((part, index) => {
        if (isWrapped(part, BOLD_MARK))
            return (
                <strong key={index}>
                    {part.slice(BOLD_MARK.length, -BOLD_MARK.length)}
                </strong>
            );
        if (isWrapped(part, CODE_MARK))
            return (
                <code key={index}>
                    {part.slice(CODE_MARK.length, -CODE_MARK.length)}
                </code>
            );
        const link = part.match(LINK_PATTERN);
        if (link)
            return (
                <a
                    href={link[2]}
                    key={index}
                    rel="noopener noreferrer"
                    target="_blank"
                >
                    {link[1]}
                </a>
            );
        return part;
    });

interface AssistantMarkdownProps {
    content: string;
}

/**
 * Minimal, safe markdown renderer for assistant replies: headings, bullets,
 * bold, inline code and http(s) links. Never renders raw HTML.
 */
export const AssistantMarkdown = ({ content }: AssistantMarkdownProps) => {
    const classes = useStyles();
    return (
        <div className={classes.markdown}>
            {content.split('\n').map((line, index) => {
                const heading = line.match(HEADING_PATTERN);
                if (heading)
                    return <h3 key={index}>{renderInline(heading[1])}</h3>;
                const bullet = line.match(BULLET_PATTERN);
                if (bullet)
                    return (
                        <div className={classes.listItem} key={index}>
                            <span>•</span>
                            <span>{renderInline(bullet[1])}</span>
                        </div>
                    );
                if (!line.trim())
                    return <div className={classes.spacer} key={index} />;
                return <p key={index}>{renderInline(line)}</p>;
            })}
        </div>
    );
};
